/**
 * =============================================================================
 * IDS CRM Web - one robot's section inside a job's Log Work
 * =============================================================================
 *
 * PURPOSE:
 * Multi-asset / multi-engineer jobs, Phases 2 and 3 (docs/MULTI_ASSET_MULTI_
 * ENGINEER_JOBS_PLAN.md, "CHECK FORMS OVERWRITING EACH OTHER", "ONE JOB
 * STATUS, SEVERAL ROBOTS", "LOG WORK ON A PHONE"). A check form's answers live
 * in one useTemplateCheckForm instance, and a hook cannot be called in a loop,
 * so a job with several robots needs one component instance per robot. This
 * is that instance: it resolves the form for ONE robot exactly as the Log Work
 * modal did for the job's single robot, renders the same block, and reports
 * its state to the modal through onStateChange so the modal can validate each
 * section and send one template_checks entry per robot.
 *
 * Phase 3 adds the robot's sign-off to the same section: Done / Not done this
 * visit (a reason when not done) and an optional health score, shown when the
 * chosen outcome closes the work. The modal owns that value (robotOutcome /
 * onRobotOutcomeChange) and sends it as one asset_outcomes entry. With several
 * robots the section always has a header naming its robot - even while the
 * form loads or when no form exists - a button to fold the body away on a
 * phone, and a tick once the robot is settled.
 *
 * Phase 6 step 4: with several robots on the job the sign-off also offers
 * "Fault found - raise a follow-up ticket for this robot" (subject,
 * description, priority), so each faulty robot gets its own ticket. The modal
 * sends it as the robot's asset_outcomes entry's follow_up. One robot on the
 * job keeps the job-level box in WorkLogOutcomeSection, as before.
 *
 * A sales_action task uses this for its stage form too: one section keyed on
 * the task, with the task's asset (if any) as the entry's asset_id, as before.
 * It never gets the robot sign-off.
 *
 * With one robot on the job it renders what it rendered before Phase 2: same
 * heading, same sentences, same resolve requests, same saved form entry. The
 * robot header only appears when the job lists several robots.
 *
 * Phase 6 step 7 (saved Log Work, spec 6.5). Four optional props, and without
 * them nothing above changes:
 *   - savedSection { form, files }: the robot's saved part. Its form is shown
 *     again from GET /check-form-templates/:template_id with its answers, tick
 *     and photos, instead of resolving a fresh form, unless it was answered
 *     under another work type or job type (then a fresh form is resolved).
 *   - uploadAttachment(stepKey, file): each photo is sent into the saved Log
 *     Work on its own; removeUploadedAttachment(attachment) takes one out.
 *   - readOnlyReason: the answers are shown, greyed, under that sentence, and
 *     the section never reports a state, so nothing in it is filed.
 *   - onPartChange(sectionKey, { outcome, note, health_score, follow_up, form })
 *     on the engineer's own changes only: never on restore or defaults.
 *
 * =============================================================================
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import TemplateCheckFormRenderer from './TemplateCheckFormRenderer';
import { checkCategoryForAssetType, maintenanceTypeForTaskType } from '../utils/checkFormTargeting';
import useTemplateCheckForm, { attachmentFromFileRef, linkSentAttachments } from '../hooks/useTemplateCheckForm';
import { formOwnsCompletion, completionTicked } from '../utils/formCompletion';

const isPlainObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

/** A saved part's form, when it names the form it was answered on. */
export const savedFormOf = (savedSection) => {
  const form = savedSection && savedSection.form;
  return isPlainObject(form) && form.template_id ? form : null;
};

/**
 * True when a saved form still belongs on this section: answered under the
 * work type now chosen and the job type the job has now. A value the saved
 * form does not record is not held against it.
 */
export const savedFormMatches = (form, { workLogType, taskType }) => Boolean(form)
  && !(form.log_type && workLogType && form.log_type !== workLogType)
  && !(form.task_type && taskType && form.task_type !== taskType);

/** A saved part's files, grouped by step as the form's photo boxes hold them. */
export const attachmentsByStep = (files) => {
  const grouped = {};
  (Array.isArray(files) ? files : []).forEach(file => {
    if (!file) return;
    const stepKey = file.step_key || '';
    grouped[stepKey] = [...(grouped[stepKey] || []), attachmentFromFileRef(file, stepKey)];
  });
  return grouped;
};

/**
 * A robot's follow-up ticket before anything is asked for (Phase 6 step 4):
 * not wanted, and every box blank. A blank priority means "Same as this job".
 */
export const emptyRobotFollowUp = () => ({ wanted: false, subject: '', description: '', priority: '' });

/** The robot sign-off a section starts with: done, no reason, no health score, no follow-up. */
export const emptyRobotOutcome = () => ({ outcome: 'done', note: '', health_score: '', follow_up: emptyRobotFollowUp() });

// The ticket subject column holds 255 characters; the server refuses more.
const FOLLOW_UP_SUBJECT_MAX = 255;

/**
 * @param {object}   props
 * @param {Function} props.authFetch       Authenticated fetch from AuthContext.
 * @param {object}   props.task            The job (task_type, source_product, source_pipeline_stage, asset_id).
 * @param {object}   props.asset           { asset_id, asset_ref, robot_type } - the robot this section is for.
 *                                         A sales task passes the task's own asset fields (asset_id may be null).
 * @param {string}   props.sectionKey      The key the modal stores this section's state under.
 * @param {string}   props.workLogType     The selected work type (formData.log_type).
 * @param {string}   props.taskTypeLabel   Label for the job type, used in the maintenance-visit sentences.
 * @param {number}   props.robotCount      How many robots the job lists; a header names the robot when > 1.
 * @param {object}   [props.robotOutcome]  { outcome: 'done' | 'not_done', note, health_score, follow_up } - this
 *                                         robot's sign-off, owned by the modal. Absent on a sales task section.
 *                                         follow_up is { wanted, subject, description, priority } (Phase 6
 *                                         step 4): with several robots on the job each robot raises its own
 *                                         follow-up ticket here, and the job-level box is hidden.
 * @param {Function} [props.onRobotOutcomeChange] (patch) => void - the modal merges the patch.
 * @param {boolean}  [props.showRobotOutcome]     True when the chosen outcome closes the work, so the
 *                                         Done / Not done controls are shown.
 * @param {Function} props.onError         (message) => void for attachment errors.
 * @param {Function} props.onStateChange   (sectionKey, state | null) => void. state = { assetId, assetRef,
 *                                         template, loading, failed, warning, completed, answers, attachments,
 *                                         hasContent, showWarning, useCheckTemplate, buildTemplateCheckEntry }.
 *                                         Called with null when the section unmounts.
 * @param {object}   [props.savedSection] Saved Log Work: { form, files }. form is the saved part's
 *                                         { template_id, template_version, answers, completed, task_type,
 *                                         log_type } (or null); files are the saved Log Work's live files
 *                                         for this part ({ file_id, client_ref, step_key, name, size, type,
 *                                         url }). Read once, when the section mounts.
 * @param {Function} [props.uploadAttachment] Saved Log Work: (stepKey, file) => Promise<FileRef>.
 * @param {Function} [props.removeUploadedAttachment] Saved Log Work: (attachment) => Promise.
 * @param {string}   [props.readOnlyReason] Shows the saved answers read-only under this sentence.
 *                                         Such a section reports no state and no part change.
 * @param {Function} [props.onPartChange]  (sectionKey, { outcome, note, health_score, follow_up, form })
 *                                         => void, on the engineer's own changes only. form is null
 *                                         while the robot has no form.
 */
function TaskAssetCheckSection({
  authFetch,
  task,
  asset,
  sectionKey,
  workLogType,
  taskTypeLabel,
  robotCount = 1,
  robotOutcome = null,
  onRobotOutcomeChange = null,
  showRobotOutcome = false,
  onError,
  onStateChange,
  savedSection = null,
  uploadAttachment = null,
  removeUploadedAttachment = null,
  readOnlyReason = null,
  onPartChange = null
}) {
  const assetId = asset?.asset_id || null;
  const assetRef = asset?.asset_ref || null;
  const robotType = asset?.robot_type;
  const readOnly = Boolean(readOnlyReason);

  // Phase 10: a sales_action task resolves its STAGE-TARGETED sales form
  // (the site survey etc.), not the asset-category check — even when the
  // task carries the minted asset.
  const isSalesActionTask = task?.task_type === 'sales_action';
  const checkCategory = isSalesActionTask
    ? (task?.source_product && task?.source_pipeline_stage ? 'sales_form' : null)
    : (assetId ? checkCategoryForAssetType(robotType) : null);

  // A Quarterly Health Check or Annual Service job is a maintenance visit:
  // Log Work looks for the scheduled-maintenance form for that visit first
  // (no service year - these jobs have no contract year), and falls back to
  // the form for the selected work type when none is set up for this asset
  // (Gareth 2026-09-15). The work log still records the chosen work type.
  const maintenanceType = checkCategory === 'sales_form' ? null : maintenanceTypeForTaskType(task?.task_type);
  const workTypeResolveParams = {
    category: checkCategory,
    workLogType,
    assetType: robotType
  };

  // Saved Log Work: the form saved on this section, when it still belongs
  // here. A read-only section shows whatever was saved.
  const savedForm = savedFormOf(savedSection);
  const restorableForm = savedForm && (readOnly || savedFormMatches(savedForm, { workLogType, taskType: task?.task_type }))
    ? savedForm
    : null;
  const savedRef = useRef(null);
  savedRef.current = { form: restorableForm, files: savedSection ? savedSection.files : null };
  // A saved form whose template could not be fetched: restored instead onto
  // the same template if /resolve finds it.
  const pendingRestoreRef = useRef(null);

  const loadSavedSection = useCallback(async () => {
    const { form, files } = savedRef.current || {};
    if (!form) return null;
    const restored = {
      answers: isPlainObject(form.answers) ? form.answers : {},
      attachments: attachmentsByStep(files),
      completed: form.completed === true
    };
    let template = null;
    try {
      const response = await authFetch(`/api/v1/check-form-templates/${encodeURIComponent(form.template_id)}`);
      if (response && response.ok) template = await response.json();
    } catch (err) {
      template = null;
    }
    if (!template || !template.template_id) {
      if (readOnly) {
        // Shown with its sentence; never resolved afresh, so nothing is filed.
        return { template: { template_id: form.template_id, unavailable: true }, ...restored };
      }
      pendingRestoreRef.current = { templateId: form.template_id, ...restored };
      throw new Error('The saved check form could not be loaded; resolving it again.');
    }
    return {
      template,
      metadata: {
        template_id: template.template_id,
        template_version: form.template_version ?? template.version,
        template_snapshot: template.schema_json
      },
      ...restored
    };
  }, [authFetch, readOnly]);

  // Template check state - resolved by Check Form Template targeting
  // (category from the asset type, workLogType from the selected work type)
  const templateCheck = useTemplateCheckForm({
    authFetch,
    enabled: readOnly ? Boolean(savedForm) : Boolean(checkCategory),
    // Saved Log Work only; without savedSection the slot resolves as before.
    ...(savedSection ? { loadSaved: loadSavedSection, skipSavedAfterParamsChange: true } : {}),
    uploadAttachment,
    removeUploadedAttachment,
    resolveParams: checkCategory === 'sales_form'
      ? {
        category: 'sales_form',
        pipelineStageProduct: task.source_product,
        pipelineStageName: task.source_pipeline_stage
      }
      : maintenanceType
        ? {
          category: checkCategory,
          workLogType: 'scheduled_maintenance',
          assetType: robotType,
          maintenanceType
        }
        : workTypeResolveParams,
    resolveFallbackParams: maintenanceType ? workTypeResolveParams : null,
    // Re-resolve fresh whenever the selected work type changes, even for the
    // sales-form params that don't include it.
    resetSignal: workLogType,
    conflictMessage: (errorData, targeting) => {
      const names = (errorData.conflicting_templates || []).map(t => t.name).join(', ');
      // On a maintenance visit the clash is between that visit's scheduled
      // forms, not the selected work type's.
      if (targeting?.workLogType === 'scheduled_maintenance') {
        return `Multiple scheduled maintenance forms match ${taskTypeLabel} jobs on this asset (${names}). An admin needs to fix the targeting in Settings before this work can be completed.`;
      }
      return `Multiple check templates target this work type and asset (${names}). An admin needs to fix the targeting in Settings before this work can be completed.`;
    },
    onError,
    logLabel: 'Task Check'
  });
  const {
    template: checkTemplate,
    loading: checkTemplateLoading,
    failed: checkTemplateFailed,
    answers: checkAnswers,
    setAnswers: setCheckAnswers,
    attachments: checkAttachments,
    setAttachments: setCheckAttachments,
    addAttachments: addCheckAttachments,
    removeAttachment: removeCheckAttachment,
    resolveWarning: checkResolveWarning,
    completed: checkCompleted,
    setCompleted: setCheckCompleted,
    usedResolveFallback: checkUsedWorkTypeForm,
    metadata: checkMetadata,
    buildTemplateCheckEntry
  } = templateCheck;

  // The saved form's template could not be fetched, but /resolve found the
  // same template: put the saved answers back on it.
  useEffect(() => {
    const pending = pendingRestoreRef.current;
    if (!pending || !checkTemplate || checkTemplateLoading) return;
    pendingRestoreRef.current = null;
    if (checkTemplate.template_id !== pending.templateId) return;
    setCheckAnswers(pending.answers);
    setCheckAttachments(pending.attachments);
    setCheckCompleted(pending.completed);
  }, [checkTemplate, checkTemplateLoading, setCheckAnswers, setCheckAttachments, setCheckCompleted]);

  // A photo still sending has no url; once the saved Log Work lists the file
  // with the same client_ref, the section takes its url and id.
  const savedFiles = savedSection ? savedSection.files : null;
  useEffect(() => {
    if (!Array.isArray(savedFiles) || savedFiles.length === 0) return;
    setCheckAttachments(prev => {
      let changed = false;
      const next = {};
      Object.entries(prev || {}).forEach(([stepKey, list]) => {
        next[stepKey] = linkSentAttachments(list, savedFiles);
        if (next[stepKey] !== list) changed = true;
      });
      return changed ? next : prev;
    });
  }, [savedFiles, setCheckAttachments]);

  const useCheckTemplate = readOnly
    ? Boolean(checkTemplate && !checkTemplate.unavailable && checkTemplate.schema_json && !checkTemplateLoading)
    : Boolean(checkCategory && checkTemplate && !checkTemplateLoading && !checkTemplateFailed);
  const showCheckWarning = Boolean(checkCategory && checkResolveWarning && !checkTemplate && !checkTemplateLoading);
  const hasContent = Object.keys(checkAnswers).length > 0 || Object.keys(checkAttachments).length > 0;

  // A form with its own completion tick owns completion, as on tickets: the
  // fixed box below is left out and the form's tick decides.
  const formTick = useCheckTemplate && formOwnsCompletion(checkTemplate.schema_json);
  useEffect(() => {
    if (!formTick || readOnly) return;
    const ticked = completionTicked(checkAnswers);
    if (ticked !== checkCompleted) setCheckCompleted(ticked);
  }, [formTick, readOnly, checkAnswers, checkCompleted, setCheckCompleted]);

  // Says which form this is, so an engineer on a maintenance visit can tell
  // the visit's own form from the standard check.
  const checkFormNote = !maintenanceType
    ? 'Required for this asset and work type. Complete all fields before saving.'
    : checkUsedWorkTypeForm
      ? `No scheduled maintenance form is set up on this asset for ${taskTypeLabel} jobs, so this is the check for the selected work type. Complete all fields before saving.`
      : `Scheduled maintenance form for this ${taskTypeLabel}. Complete all fields before saving.`;

  // The modal reads this at submit time: it validates each section and builds
  // one template_checks entry per section with content.
  useEffect(() => {
    if (!onStateChange) return undefined;
    // Answers kept for reference are never filed.
    if (readOnly) {
      onStateChange(sectionKey, null);
      return undefined;
    }
    onStateChange(sectionKey, {
      assetId,
      assetRef,
      template: checkTemplate,
      loading: checkTemplateLoading,
      failed: checkTemplateFailed,
      warning: checkResolveWarning,
      completed: checkCompleted,
      answers: checkAnswers,
      attachments: checkAttachments,
      hasContent,
      showWarning: showCheckWarning,
      useCheckTemplate,
      buildTemplateCheckEntry: () => buildTemplateCheckEntry(assetId)
    });
    return undefined;
  }, [
    onStateChange, sectionKey, assetId, assetRef, checkTemplate, checkTemplateLoading, checkTemplateFailed,
    checkResolveWarning, checkCompleted, checkAnswers, checkAttachments, hasContent, showCheckWarning,
    useCheckTemplate, buildTemplateCheckEntry, readOnly
  ]);

  // A section that leaves the modal takes its state with it.
  useEffect(() => () => {
    if (onStateChange) onStateChange(sectionKey, null);
  }, [onStateChange, sectionKey]);

  // Phase 3: the robot's sign-off. A sales task section has none; a robot
  // section gets the controls only when the chosen outcome closes the work.
  const outcome = robotOutcome || emptyRobotOutcome();

  // Saved Log Work: the part this section saves, from the latest state plus
  // the engineer's change. A form still loading keeps the saved one, so a tap
  // on Done / Not done never empties answers not yet on screen.
  const hasSignOff = !isSalesActionTask && Boolean(assetId);
  const latestRef = useRef(null);
  latestRef.current = {
    answers: checkAnswers,
    completed: checkCompleted,
    robotOutcome,
    liveForm: checkTemplate && !checkTemplate.unavailable && !checkTemplateLoading && !checkTemplateFailed
      ? {
        template_id: checkMetadata?.template_id || checkTemplate.template_id,
        template_version: checkMetadata?.template_version ?? checkTemplate.version ?? null,
        task_type: task?.task_type || null,
        log_type: workLogType || null
      }
      : null,
    restorableForm
  };
  const reportedRef = useRef(false);
  const reportPart = (change = {}) => {
    if (!onPartChange || readOnly) return;
    const latest = latestRef.current;
    const answers = change.answers !== undefined ? change.answers : latest.answers;
    const completed = change.completed !== undefined ? change.completed : latest.completed;
    const signOff = { ...emptyRobotOutcome(), ...(latest.robotOutcome || {}), ...(change.outcome || {}) };
    const form = latest.liveForm
      ? { ...latest.liveForm, answers: answers || {}, completed: Boolean(completed) }
      : (latest.restorableForm || null);
    reportedRef.current = true;
    onPartChange(sectionKey, {
      outcome: hasSignOff ? (signOff.outcome || null) : null,
      note: hasSignOff ? (signOff.note || '') : null,
      health_score: hasSignOff ? (signOff.health_score || '') : null,
      follow_up: hasSignOff ? { ...emptyRobotFollowUp(), ...(signOff.follow_up || {}) } : null,
      form
    });
  };

  const changeAnswers = (next) => {
    setCheckAnswers(next);
    reportPart(formTick ? { answers: next, completed: completionTicked(next) } : { answers: next });
  };
  const changeCompleted = (next) => {
    setCheckCompleted(next);
    reportPart({ completed: next });
  };
  // A photo is not part of the saved part, but the first one saves the form
  // it belongs to, so the photo comes back with its form after a reload.
  const addPhotos = async (stepKey, files, inputElement) => {
    await addCheckAttachments(stepKey, files, inputElement);
    if (uploadAttachment && !reportedRef.current && !restorableForm) reportPart();
  };

  const patchOutcome = (patch) => {
    if (onRobotOutcomeChange) onRobotOutcomeChange(patch);
    reportPart({ outcome: patch });
  };
  const showOutcomeControls = Boolean(!readOnly && showRobotOutcome && !isSalesActionTask && assetId && onRobotOutcomeChange);
  const notDone = showOutcomeControls && outcome.outcome === 'not_done';
  // Phase 6 step 4: with several robots each robot raises its own follow-up
  // ticket, Done or Not done. One robot keeps the job-level box, as before.
  const showFollowUp = showOutcomeControls && robotCount > 1;
  const followUp = { ...emptyRobotFollowUp(), ...(outcome.follow_up || {}) };
  const patchFollowUp = (patch) => patchOutcome({ follow_up: { ...followUp, ...patch } });

  // With several robots the section always has a header, folds away, and
  // shows a tick once the robot is settled: Done with its form complete (or
  // no form to fill), or Not done with a reason.
  const hasHeader = robotCount > 1 && Boolean(assetRef);
  const [open, setOpen] = useState(true);
  const formSettled = !checkCategory
    || (!checkTemplateLoading && !showCheckWarning && (!useCheckTemplate || checkCompleted));
  const settled = !readOnly && (notDone ? outcome.note.trim().length > 0 : formSettled);
  const templateUnavailable = Boolean(readOnly && checkTemplate && checkTemplate.unavailable);

  const rendersAnything = hasHeader || useCheckTemplate || showCheckWarning || showOutcomeControls || readOnly;
  if (!rendersAnything) return null;

  const idBase = `robot-section-${sectionKey}`;
  const typeLabel = robotType ? String(robotType).replace(/_/g, ' ') : '';

  return (
    <div className="form-section template-check-section">
      {hasHeader && (
        <div className="robot-section-header">
          <div className="robot-section-title">
            <span className="template-check-robot">{assetRef}</span>
            {typeLabel && <span className="robot-section-type"> · {typeLabel}</span>}
            {settled && (
              <span className="robot-section-tick" role="img" aria-label={`${assetRef} settled`} title="Settled">✓</span>
            )}
          </div>
          <button
            type="button"
            className="robot-section-toggle"
            aria-expanded={open}
            aria-controls={`${idBase}-body`}
            onClick={() => setOpen(prev => !prev)}
          >
            {open ? 'Hide' : 'Show'}
          </button>
        </div>
      )}

      {open && (
        <div id={`${idBase}-body`} className="robot-section-body">
          {readOnly && (
            <div className="modal-hint robot-section-read-only" role="note">{readOnlyReason}</div>
          )}

          {templateUnavailable && (
            <div className="modal-hint">The form these answers were given on could not be loaded, so they cannot be shown here.</div>
          )}

          {showCheckWarning && (
            <div className="modal-error">{checkResolveWarning}</div>
          )}

          {useCheckTemplate && (
            <>
              <div className="form-section-header">
                <h3>{checkTemplate.name || checkTemplate.schema_json?.name || 'Check Form'}</h3>
                {!readOnly && <p>{checkFormNote}</p>}
              </div>
              <TemplateCheckFormRenderer
                schema={checkTemplate.schema_json}
                answers={checkAnswers}
                onAnswersChange={onPartChange ? changeAnswers : setCheckAnswers}
                disabled={readOnly}
                stepAttachments={checkAttachments}
                onAddPhotos={readOnly ? undefined : (onPartChange ? addPhotos : addCheckAttachments)}
                onRemovePhoto={readOnly ? undefined : removeCheckAttachment}
              />
              {!formTick && (
                <label className="template-check-complete">
                  <input
                    type="checkbox"
                    checked={checkCompleted}
                    disabled={readOnly}
                    onChange={e => (onPartChange ? changeCompleted(e.target.checked) : setCheckCompleted(e.target.checked))}
                  />
                  Check complete - all items answered and accurate
                </label>
              )}
            </>
          )}

          {showOutcomeControls && (
            <div className="robot-outcome">
              <div className="form-group robot-outcome-choice" role="radiogroup" aria-label={`${assetRef || 'Robot'} this visit`}>
                <label>
                  <input
                    type="radio"
                    name={`${idBase}-outcome`}
                    value="done"
                    checked={outcome.outcome !== 'not_done'}
                    onChange={() => patchOutcome({ outcome: 'done' })}
                  />
                  Done this visit
                </label>
                <label>
                  <input
                    type="radio"
                    name={`${idBase}-outcome`}
                    value="not_done"
                    checked={outcome.outcome === 'not_done'}
                    onChange={() => patchOutcome({ outcome: 'not_done' })}
                  />
                  Not done this visit
                </label>
              </div>
              {notDone && (
                <div className="form-group">
                  <label htmlFor={`${idBase}-note`}>Why not? *</label>
                  <textarea
                    id={`${idBase}-note`}
                    value={outcome.note}
                    onChange={e => patchOutcome({ note: e.target.value })}
                    rows={2}
                    placeholder="Why was this robot not done on this visit?"
                  />
                </div>
              )}
              <div className="form-group">
                <label htmlFor={`${idBase}-health`}>Robot health</label>
                <select
                  id={`${idBase}-health`}
                  value={outcome.health_score}
                  onChange={e => patchOutcome({ health_score: e.target.value })}
                >
                  <option value="">Not recorded</option>
                  <option value="green">Green</option>
                  <option value="amber">Amber</option>
                  <option value="red">Red</option>
                </select>
              </div>
              {showFollowUp && (
                <div className="robot-follow-up">
                  <div className="form-group checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={followUp.wanted}
                        onChange={e => patchFollowUp({ wanted: e.target.checked })}
                      />
                      Fault found - raise a follow-up ticket for this robot
                    </label>
                  </div>
                  {followUp.wanted && (
                    <>
                      <div className="form-group">
                        <label htmlFor={`${idBase}-follow-up-subject`}>Follow-up subject</label>
                        <input
                          id={`${idBase}-follow-up-subject`}
                          type="text"
                          value={followUp.subject}
                          maxLength={FOLLOW_UP_SUBJECT_MAX}
                          onChange={e => patchFollowUp({ subject: e.target.value })}
                          placeholder={`Fault found on ${assetRef || 'this robot'} during ${task?.task_ref || 'this job'}`}
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor={`${idBase}-follow-up-description`}>Follow-up description</label>
                        <textarea
                          id={`${idBase}-follow-up-description`}
                          value={followUp.description}
                          onChange={e => patchFollowUp({ description: e.target.value })}
                          rows={2}
                          placeholder="What needs looking at?"
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor={`${idBase}-follow-up-priority`}>Follow-up priority</label>
                        <select
                          id={`${idBase}-follow-up-priority`}
                          value={followUp.priority}
                          onChange={e => patchFollowUp({ priority: e.target.value })}
                        >
                          <option value="">Same as this job</option>
                          <option value="low">Low</option>
                          <option value="normal">Normal</option>
                          <option value="high">High</option>
                          <option value="critical">Critical</option>
                        </select>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default TaskAssetCheckSection;
